import { Injectable, effect, inject, signal, untracked } from '@angular/core';

import { CompanyApiService } from 'src/app/api/services/company-api.service';
import { CompanyTreeService } from 'src/app/app-common/services/company-tree.service';
import { CompanyNode } from 'src/app/app-common/types/company-tree';
import {
  ScrollingListFactoryService,
  ScrollingListInstance,
  ScrollingListSearch,
} from 'src/app/components/scrolling-list/services/scrolling-list-factory.service';
import { AddressUtilsService } from 'src/app/shared/services/address-utils.service';
import { CompanyTreeItem } from 'src/app/settings/services/company';

const SEARCH_PAGE_SIZE = 40;

type SearchCompany = Pick<
  CompanyTreeItem,
  | 'id'
  | 'name'
  | 'parentId'
  | 'parentName'
  | 'street'
  | 'city'
  | 'province'
  | 'displayCount'
>;

@Injectable()
export class CompanyTreeSearchService {
  private readonly companyApi = inject(CompanyApiService);
  private readonly treeService = inject(CompanyTreeService);
  private readonly addressUtils = inject(AddressUtilsService);
  private readonly scrollingListFactory = inject(ScrollingListFactoryService);

  readonly search: ScrollingListSearch;

  private readonly list: ScrollingListInstance<SearchCompany>;
  private readonly queryState = signal('');
  private readonly resultsState = signal<CompanyNode[]>([]);
  private readonly loadingState = signal(false);
  private readonly errorMessageState = signal<string | null>(null);
  private requestedQuery: string | null = null;
  private requestedRootId: string | null = null;
  private scopedRootId: string | null = null;

  readonly query = this.queryState.asReadonly();
  readonly results = this.resultsState.asReadonly();
  readonly loading = this.loadingState.asReadonly();
  readonly errorMessage = this.errorMessageState.asReadonly();

  constructor() {
    this.search = {
      // Read per search, never here: this service is constructed before the
      // tree service is initialized, so the display root is still null.
      companyId: null,
      includeSubcompanies: true,
      query: '',
      sortBy: 'name',
      count: SEARCH_PAGE_SIZE,
      skipFirstLoad: true,
      name: 'Companies',
      onListUpdate: () => this.syncFromList(),
    };
    this.list = this.scrollingListFactory.create(
      this.companyApi.getCompanies.bind(this.companyApi),
      this.search,
    );

    // Seeded so the effect's own first run is not mistaken for a re-rooting.
    this.scopedRootId = untracked(() => this.treeService.displayRootId());

    // Flipping the toggle with a query in the field must re-run it against the
    // new scope; a query left showing the old root's matches contradicts the
    // toggle the user just moved.
    effect(() => {
      const root = this.treeService.displayRootId();

      untracked(() => {
        const rerooted = root !== this.scopedRootId;
        this.scopedRootId = root;

        if (!rerooted || !this.queryState()) {
          return;
        }

        this.resetResults();
        this.doSearch();
      });
    });
  }

  doSearch = (): void => {
    const rootId = this.treeService.displayRootId();
    this.search.companyId = rootId;
    const query =
      typeof this.search.query === 'string' ? this.search.query : '';
    this.queryState.set(query);

    if (!query || !rootId) {
      this.requestedQuery = null;
      this.requestedRootId = null;
      this.resetResults();
      return;
    }

    this.loadingState.set(true);
    this.errorMessageState.set(null);

    // The list factory ignores a load while one is already running, so a query
    // issued now would never reach the server. `syncFromList` re-issues it.
    if (this.list.loadingItems) {
      return;
    }

    this.requestedQuery = query;
    this.requestedRootId = rootId;
    this.list.doSearch();
  };

  load = (): void => {
    if (!this.queryState() || !this.search.companyId) {
      return;
    }

    this.list.load();
    this.loadingState.set(this.list.loadingItems);
  };

  private reissuePendingSearch(): void {
    const rootId = this.treeService.displayRootId();
    this.search.companyId = rootId;
    const pending = this.queryState();
    if (!pending || !rootId) {
      return;
    }

    this.search.query = pending;
    this.requestedQuery = pending;
    this.requestedRootId = rootId;
    this.loadingState.set(true);
    this.errorMessageState.set(null);
    this.list.doSearch();
  }

  private resetResults(): void {
    this.list.items.clear();
    this.list.errorMessage = '';
    this.list.apiError = '';
    this.list.errorCodes = [];
    this.loadingState.set(false);
    this.errorMessageState.set(null);
    this.resultsState.set([]);
  }

  private syncFromList(): void {
    // A response can still land after the query was cleared: `doSearch` resets
    // the state but cannot cancel a request already in flight, and the factory
    // repopulates `list.items` when it settles. Discard that response instead
    // of restoring results — and a stale error — for an abandoned query.
    if (!this.queryState() || !this.search.companyId) {
      this.requestedQuery = null;
      this.requestedRootId = null;
      this.resetResults();
      return;
    }

    const displayRootId = this.treeService.displayRootId();

    // A settled response answers the query and the root that were in flight.
    // When either has moved on since — the factory refuses a load while one is
    // running, so a re-rooting mid-request never reached the server — re-issue
    // rather than publish the old root's matches under the new scope.
    if (
      !this.list.loadingItems &&
      (this.requestedQuery !== this.queryState() ||
        this.requestedRootId !== displayRootId)
    ) {
      this.reissuePendingSearch();
      return;
    }

    // The display root is the tree's own first row, so a result repeating it
    // would duplicate the row the search replaced.
    const results = this.list.items.list
      .filter((item) => item?.id && item.id !== displayRootId)
      .map((item) => this.toResult(item));

    this.resultsState.set(results);
    this.loadingState.set(this.list.loadingItems);
    this.errorMessageState.set(this.list.apiError ? this.list.apiError : null);
  }

  private toResult(company: SearchCompany): CompanyNode {
    const result: CompanyNode = {
      id: company.id ?? '',
      name: company.name ?? '',
      parentId: company.parentId ?? null,
      address: this.addressUtils.formatShortAddress(company),
    };

    // Absent is not zero: a company we were told nothing about must not claim
    // it has no displays.
    if (Number.isFinite(company.displayCount)) {
      result.displayCount = company.displayCount;
    }

    const parentName =
      typeof company.parentName === 'string' ? company.parentName.trim() : '';
    if (parentName) {
      result.parentName = parentName;
    }

    return result;
  }
}
